/**
 * fundraiserUtils.ts - Fundraiser Data Processing Utilities
 *
 * This utility module provides functions for processing and extracting data from
 * fundraiser objects, including media handling, image processing, and data normalization.
 *
 * Key Functions:
 * - getPrimaryFundraiserImage: Gets the main display image for media sections
 * - getFundraiserLogoImage: Gets the header logo/icon image
 * - getFundraiserIconChoice: Determines icon display mode
 * - getFundraiserMediaItems: Processes media items with fallbacks
 *
 * Features:
 * - Fallback image handling for missing media
 * - Legacy data format support
 * - Media type processing (image/video)
 * - Default media item generation
 *
 * Dependencies: Fundraiser type definitions
 */

import { IconType } from "../enums";
import { CreateFundraiserModel, MediaModel } from "../models";
import { Fundraiser } from "../models/FundraisersGridModels";

/**
 * Gets the primary image URL for a fundraiser
 * Gets the first media item image, separate from logo/icon
 */
export const getPrimaryFundraiserImage = (
  fundraiser: CreateFundraiserModel
): MediaModel => {
  // Get primary image from mediaItems array (for media gallery)
  const primaryMedia =
    fundraiser?.mediaItems &&
      Array.isArray(fundraiser?.mediaItems) &&
      fundraiser.mediaItems.length > 0
      ? fundraiser.mediaItems[0]
      : null;
  if (primaryMedia?.fullURL) {
    return primaryMedia;
  }

  // Fallback to image property if it exists (legacy support)
  if (fundraiser.fundraiserImage) {
    return fundraiser.fundraiserImage;
  }

  // Default fallback image
  return new MediaModel();
};

/**
 * Gets the logo/icon image URL for a fundraiser header
 * This is separate from the primary media image
 */
export const getFundraiserLogoImage = (
  fundraiser: Fundraiser
): MediaModel | null => {
  // Check if fundraiser has logo-specific data
  if (fundraiser.fundraiserImage) {
    return fundraiser.fundraiserImage;
  }

  return null;
};

/**
 * Gets the icon choice for a fundraiser header
 */
export const getFundraiserIconChoice = (fundraiser: Fundraiser): IconType => {
  return fundraiser.iconImageChoice as IconType|| IconType.None;
};

/**
 * Gets all media items for a fundraiser, ensuring primary image is included
 */
export const getFundraiserMediaItems = (fundraiser: CreateFundraiserModel) => {
  const mediaItems = fundraiser.mediaItems || [];
  const primaryImage = getPrimaryFundraiserImage(fundraiser);

  // If mediaItems is empty, create a default media item
  if (mediaItems.length === 0) {
    return primaryImage ? [new MediaModel({ ...primaryImage })] : [];
  }

  return mediaItems;
};

export const getDetailPageMediaItems = (fundraiser: Fundraiser) => {
  const mediaItems = fundraiser.mediaItems || [];
  const primaryImage = fundraiser.fundraiserImage;

  // If mediaItems is empty, create a default media item
  if (mediaItems.length === 0) {
    return primaryImage ? [new MediaModel({ ...primaryImage })] : [];
  }

  return mediaItems;
};
